import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AlertTriangle, ArrowRight, Check, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { WavePlanningGrid } from '@/components/WavePlanning/WavePlanningGrid'
import { WavePlanningChart } from '@/components/WavePlanning/WavePlanningChart'
import { WavePlanningSummary } from '@/components/WavePlanning/WavePlanningSummary'
import { WavePlanParametersDrawer } from '@/components/WavePlanning/WavePlanParametersDrawer'
import { CoreReceiptsCard } from '@/components/WavePlanning/CoreReceiptsCard'
import {
  formatPercent,
  PERCENT_DECIMALS,
  weekLabel,
} from '@/components/WavePlanning/model'
import {
  toPlanningPanel,
  toPlanningResult,
  toTimeline,
} from '@/components/WavePlanning/adapters'
import {
  WavePlanLoadError,
  WavePlanLoading,
  WavePlanPageShell,
} from '@/components/WavePlanning/WavePlanPageShell'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { useReturnFocus } from '@/hooks/useReturnFocus'
import {
  wavePlanMfpLabel,
  wavePlanPath,
} from '@/components/WavePlanning/routes'
import {
  useResetWavePlanWeeks,
  useUpdateWavePlanCoreReceipts,
  useUpdateWavePlanWeeks,
  useWavePlan,
  useWavePlanWeeks,
} from '@/hooks/useWavePlans'

// Plan warnings shown as banners above the grid (message from the backend).
const BANNER_FLAGS = new Set(['WAVE_GAP_LT_N_WEEKS', 'DEMAND_CURVE_MISSING'])

export default function WavePlanning() {
  const { planId } = useParams()
  const detail = useWavePlan(planId)
  const weeks = useWavePlanWeeks(planId)
  const update = useUpdateWavePlanWeeks(planId ?? '')
  const reset = useResetWavePlanWeeks(planId ?? '')
  const receipts = useUpdateWavePlanCoreReceipts(planId ?? '')
  const [confirmReset, setConfirmReset] = useState(false)
  const [showParameters, setShowParameters] = useState(false)
  const focus = useReturnFocus()
  const parametersFocus = useReturnFocus()

  const panel = useMemo(
    () =>
      detail.data && weeks.data
        ? toPlanningPanel(detail.data, weeks.data.weeks)
        : null,
    [detail.data, weeks.data]
  )
  const result = useMemo(
    () =>
      weeks.data
        ? toPlanningResult(weeks.data.summary, weeks.data.weeks)
        : null,
    [weeks.data]
  )
  const timeline = useMemo(
    () => (weeks.data ? toTimeline(weeks.data.weeks) : []),
    [weeks.data]
  )

  if (detail.isError || weeks.isError)
    return (
      <WavePlanLoadError
        error={detail.error ?? weeks.error}
        onRetry={() => {
          void detail.refetch()
          void weeks.refetch()
        }}
      />
    )
  if (!detail.data || !weeks.data || !panel || !result)
    return <WavePlanLoading />

  const plan = detail.data
  const summary = weeks.data.summary
  const year = plan.ap_header.fiscal_year ?? new Date().getFullYear()
  const balanced = summary.balanced
  const core = plan.calc_path === 'CORE'
  // Core plans split the AP quantity into initial and backup receipts with the
  // planner's percentages, unless the split comes from the AP per SKU.
  const defaultEditable = summary.default_editable !== false
  // Edits wait while the overall view itself reloads (other views do not matter).
  const editable =
    plan.permissions.can_edit && !weeks.isFetching && defaultEditable
  const threshold =
    summary.variance_threshold_percent == null
      ? null
      : summary.variance_threshold_percent / 100
  const nextWave =
    timeline.find(week => week.id === summary.next_wave_week_id) ?? null
  // The next-wave timing follows the metrics basis: what the SKUs plan, by default.
  const basisTotal =
    result.skuBasis && result.skuTotal !== null ? result.skuTotal : result.total

  return (
    <WavePlanPageShell
      detail={plan}
      title="Wave Planning"
      subtitle={
        core
          ? 'Schedule the assortment plan’s Core quantities into initial and backup receipts.'
          : 'Plan weekly receipts and compare them with seasonal demand.'
      }
    >
      <Card className="gap-4 p-4 shadow-none">
        <dl className="grid gap-4 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-xs text-muted-foreground">Hierarchy</dt>
            <dd className="mt-1 font-medium">
              {[
                plan.hierarchy?.department,
                plan.hierarchy?.class,
                plan.hierarchy?.subclass,
              ]
                .filter(Boolean)
                .join(' · ') || '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Season</dt>
            <dd className="mt-1 font-medium">
              {plan.season_name ?? '—'} · FY{year}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">MFP Plan</dt>
            <dd className="mt-1 font-medium">{wavePlanMfpLabel(plan)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Snapshot</dt>
            <dd className="mt-1 font-medium">
              {plan.refreshed_at
                ? new Date(plan.refreshed_at).toLocaleString()
                : '—'}
            </dd>
          </div>
        </dl>
        <div className="flex flex-wrap items-center justify-end gap-3 border-t pt-3">
          <Button asChild size="sm">
            <Link to={wavePlanPath(plan.id, 'skus')}>
              View SKU detail <ArrowRight size={14} />
            </Link>
          </Button>
        </div>
      </Card>

      <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(340px,0.9fr)_minmax(0,1.6fr)]">
        <WavePlanningSummary
          panel={panel}
          result={result}
          year={year}
          setWeek={timeline[panel.setIndex] ?? null}
          setEventCode={summary.set_event_code}
          varianceThreshold={threshold}
          nextWave={nextWave}
          core={core}
          onShowParameters={() => {
            parametersFocus.remember()
            setShowParameters(true)
          }}
        />
        <WavePlanningChart weeks={result.weeks} setIndex={panel.setIndex} />
      </div>

      {summary.core_receipts && (
        <CoreReceiptsCard
          weeks={weeks.data.weeks}
          receipts={summary.core_receipts}
          readOnly={!plan.permissions.can_edit || weeks.isFetching}
          pending={receipts.isPending}
          onChange={change => receipts.mutate(change)}
        />
      )}

      <Card className="min-w-0 overflow-hidden shadow-none">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold">Weekly wave plan</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {!defaultEditable
                ? 'Core plan: each SKU’s initial and backup quantities come from the assortment plan. Choose the receipt weeks above; change single SKUs in the SKU detail.'
                : !plan.permissions.can_edit
                  ? 'Read-only: the wave plan is submitted, approved or frozen.'
                  : core
                    ? 'Core plan: enter the initial and backup split. Every SKU starts from it; change single SKUs in the SKU detail.'
                    : 'Edit the highlighted percentages. Results are recalculated on save.'}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {result.skuBasis
                ? 'Receipts, inventory and the receipt-to-sales variance use the SKU-calculated waves, so SKU changes move them and the next-wave timing.'
                : 'Receipts, inventory and the receipt-to-sales variance use the plan percentages.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div
              role="status"
              className={`flex items-center gap-1.5 text-xs tabular-nums ${balanced ? 'text-foreground' : 'text-destructive'}`}
            >
              {balanced ? <Check size={14} /> : <AlertTriangle size={14} />}
              <span>
                {/* Unbalanced totals show every stored decimal: never "100% · 0% remaining". */}
                <strong data-testid="wave-total">
                  {formatPercent(result.total, balanced ? 2 : PERCENT_DECIMALS)}
                </strong>{' '}
                allocated
                {!balanced &&
                  ` · ${formatPercent(Math.abs(1 - result.total), PERCENT_DECIMALS)} ${result.total < 1 ? 'remaining' : 'over target'}`}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              hidden={!defaultEditable}
              disabled={!editable || reset.isPending}
              onClick={() => {
                focus.remember()
                setConfirmReset(true)
              }}
            >
              <RotateCcw size={14} />
              Reset to default
            </Button>
          </div>
        </div>
        {nextWave && (
          <p
            role="status"
            className="flex items-center gap-2 border-b bg-amber-50 px-4 py-2 text-xs text-amber-900"
          >
            <AlertTriangle size={14} className="shrink-0" />
            {formatPercent(Math.max(0, 1 - basisTotal), PERCENT_DECIMALS)} is
            not planned yet. The receipt-to-sales variance falls below{' '}
            {formatPercent(threshold ?? 0, 0)} in {weekLabel(nextWave.week)} (
            {nextWave.event}): the recommended week for the next wave.
          </p>
        )}
        {result.belowPogSetCount > 0 && (
          <p
            role="status"
            data-testid="pog-set-warning"
            className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b bg-amber-50 px-4 py-2 text-xs text-amber-900"
          >
            <AlertTriangle size={14} className="shrink-0" />
            {result.belowPogSetCount}{' '}
            {result.belowPogSetCount === 1 ? 'SKU doesn’t' : 'SKUs don’t'} ship
            their POG set in their first wave (SKUs you changed are never raised
            automatically).
            <Link
              to={wavePlanPath(plan.id, 'skus')}
              className="font-medium underline underline-offset-2"
            >
              Review them in the SKU detail
            </Link>
          </p>
        )}
        {summary.below_minimum && (
          <p
            role="status"
            data-testid="below-minimum-warning"
            className="flex items-center gap-2 border-b bg-amber-50 px-4 py-2 text-xs text-amber-900"
          >
            <AlertTriangle size={14} className="shrink-0" />
            First wave is {formatPercent(result.firstWave)}, below the
            recommended {formatPercent(result.minimumFirstWave)} (POG set share
            plus the buffer). You can still submit.
          </p>
        )}
        {weeks.data.flags
          .filter(flag => BANNER_FLAGS.has(flag.code))
          .map(flag => (
            <p
              key={`${flag.code}-${flag.week_id}`}
              role="status"
              className="flex items-center gap-2 border-b bg-amber-50 px-4 py-2 text-xs text-amber-900"
            >
              <AlertTriangle size={14} className="shrink-0" />
              {flag.message}
            </p>
          ))}
        <WavePlanningGrid
          weeks={result.weeks}
          timeline={timeline}
          year={year}
          setIndex={panel.setIndex}
          readOnly={!editable}
          varianceThreshold={threshold}
          skuBasis={result.skuBasis}
          onChange={(weekId, percent) =>
            update.mutate({
              weekId,
              percent: percent.trim() ? Number(percent) : 0,
            })
          }
        />
      </Card>
      <WavePlanParametersDrawer
        planId={plan.id}
        open={showParameters}
        onOpenChange={setShowParameters}
        onCloseAutoFocus={parametersFocus.restore}
      />
      <ConfirmationDialog
        open={confirmReset}
        onOpenChange={setConfirmReset}
        title="Reset to default"
        description={
          core
            ? "Replaces every week's percentage with the suggested Core split: the configured initial share on the suggested initial receipt week (the set week by default) and the rest on the backup week (2 weeks later by default). SKU and DC changes are kept."
            : "Replaces every week's percentage with the recommended default. The set week gets the larger of the minimum first wave (POG set share plus the seasonal buffer) and what keeps receipts in stores at least the receipt-to-sales threshold above sales until the minimum wave gap. The rest goes in the first week the variance falls below the threshold. SKU and DC changes are kept."
        }
        confirmText="Reset"
        cancelText="Cancel"
        variant="destructive"
        isLoading={reset.isPending}
        onCloseAutoFocus={focus.restore}
        onConfirm={() =>
          reset.mutate(undefined, { onSuccess: () => setConfirmReset(false) })
        }
      />
    </WavePlanPageShell>
  )
}
